import type { components } from '@trackercop/contracts';

type SystemHealth = components['schemas']['SystemHealth'];
type HealthStatus = components['schemas']['HealthStatus'];

interface SilentKillerBannerProps {
  health: SystemHealth;
}

// Banner reflects the worst-of reduction the server already performs in
// `SystemHealth.overall` (the domain `worstOf` reducer). When overall is
// `ok` or `unknown` the banner is hidden — silent-killer signals are
// `warning` or `critical` only.
export function SilentKillerBanner({ health }: SilentKillerBannerProps) {
  const { overall, components: comps } = health;
  if (overall !== 'warning' && overall !== 'critical') return null;

  const palette =
    overall === 'critical'
      ? 'border-red-300 bg-red-50 text-red-800'
      : 'border-amber-300 bg-amber-50 text-amber-800';

  const breached = comps.filter(
    (c) => c.status === 'warning' || c.status === 'critical',
  );
  const headline =
    overall === 'critical'
      ? 'Critical issue detected'
      : 'Warning — degraded component';

  return (
    <div
      role="alert"
      className={`flex flex-col gap-2 rounded-md border px-4 py-3 text-sm ${palette}`}
      data-testid="silent-killer-banner"
      data-severity={overall satisfies HealthStatus}
    >
      <strong>{headline}</strong>
      {breached.length > 0 && (
        <ul className="flex flex-col gap-1.5 text-xs">
          {breached.map((c) => (
            <li
              key={c.name}
              className="flex flex-col gap-0.5"
              data-testid={`silent-killer-breached-${c.name}`}
            >
              <span>
                <span className="font-mono font-semibold">{c.name}</span>
                <span className="opacity-70"> · {c.status}</span>
              </span>
              <span className="opacity-80">
                {c.message ?? 'No diagnostic message — see the component tile below.'}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
